import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Shot, { shotProps } from './Shot';
import { FEATURE_ROW_IDS } from '../../shared/siteContent';
import { useSite } from './useSiteContent';

/**
 * The feature tour: one row per capability area, each with a real screenshot
 * (`scripts/marketing-shots*.mjs`, or an upload that replaced it).
 *
 * R13 lays it out as the handoff's `ABC6-02`: a sticky index of the nine
 * areas down the left (from 1100px up; each entry jumps to its row and the
 * one in view is lit), the words beside the shot.
 *
 * The words and each row's shot are Site content's (078), edited and
 * published in Platform › Site content › Site › Features; the launch copy
 * lives on as the fallback in shared/siteContent.ts. The nine rows, their
 * order and their ids are fixed — the ids are anchors (/features#access is
 * linked from the FAQ) — and so is the closing band.
 */

/** Which row is in view, for the index — nothing where the browser cannot say. */
function useRowInView(ids: string[]): string {
  const [current, setCurrent] = useState(ids[0]);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const seen = new Map<string, boolean>();
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) seen.set(e.target.id, e.isIntersecting);
      const first = ids.find((id) => seen.get(id));
      if (first) setCurrent(first);
    }, { rootMargin: '-30% 0px -55% 0px' });
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    }
    return () => io.disconnect();
  }, [ids]);
  return current;
}

/** Fixed, like the rows they name: the index observes them once. */
const IDS: string[] = [...FEATURE_ROW_IDS];

export default function FeaturesPage() {
  const { doc, shot } = useSite();
  const { rows } = doc.features;
  const current = useRowInView(IDS);
  return (
    <div className="site-page features-page" data-frame="column">
      <div className="features-layout">
        <nav className="features-index" aria-label="Features">
          {rows.map((r) => (
            <a key={r.id} href={`#${r.id}`} className={current === r.id ? 'on' : ''} aria-current={current === r.id ? 'true' : undefined}>
              {r.short}
            </a>
          ))}
        </nav>
        <div className="features-main">
          <div className="features-intro">
            <h1 className="site-h1">{doc.features.h1}</h1>
            {doc.features.sub && <p className="site-sub">{doc.features.sub}</p>}
          </div>

          {rows.map((r) => (
            <section key={r.id} id={r.id} className="feature-row" aria-labelledby={`${r.id}-title`}>
              <div className="copy">
                <h2 id={`${r.id}-title`}>{r.title}</h2>
                {r.lede && <p>{r.lede}</p>}
                {r.points.length > 0 && <ul>{r.points.map((pt, i) => <li key={i}>{pt}</li>)}</ul>}
              </div>
              <Shot {...shotProps(shot(r.shot))} />
            </section>
          ))}

          <div className="site-cta">
            <div className="text">
              <b>See it with your own form</b>
              <span>The Free plan includes the whole builder. Bring a real form and try it.</span>
            </div>
            <Link className="fs-btn neutral" to="/pricing">See plans and get started</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
